import { SubmissionStatus } from 'interfaces';
import { CLAIM_STATUS } from '@billing/validation/baseClaim';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';

export type StatusChipFamilyKey =
  | 'green'
  | 'gray'
  | 'amber'
  | 'teal'
  | 'blue'
  | 'purple';

export interface StatusChipTone {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  accentColor: string;
}

export interface StatusChipFamily {
  light: StatusChipTone;
  dark: StatusChipTone;
}

export const STATUS_CHIP_PALETTE: Record<
  StatusChipFamilyKey,
  StatusChipFamily
> = {
  green: {
    light: {
      backgroundColor: '#EAF3DE',
      borderColor: '#97C459',
      textColor: '#27500A',
      accentColor: '#3B6D11',
    },
    dark: {
      backgroundColor: '#1F3208',
      borderColor: '#639922',
      textColor: '#D0FD97',
      accentColor: '#97C459',
    },
  },
  gray: {
    light: {
      backgroundColor: '#F1EFE8',
      borderColor: '#B4B2A9',
      textColor: '#444441',
      accentColor: '#5F5E5A',
    },
    dark: {
      backgroundColor: '#2E2D2A',
      borderColor: '#5F5E5A',
      textColor: '#D3D1C7',
      accentColor: '#B4B2A9',
    },
  },
  amber: {
    light: {
      backgroundColor: '#FAEEDA',
      borderColor: '#EF9F27',
      textColor: '#633806',
      accentColor: '#BA7517',
    },
    dark: {
      backgroundColor: '#3C2608',
      borderColor: '#BA7517',
      textColor: '#FAC775',
      accentColor: '#FAC775',
    },
  },
  teal: {
    light: {
      backgroundColor: '#E1F5EE',
      borderColor: '#5DCAA5',
      textColor: '#085041',
      accentColor: '#0F6E56',
    },
    dark: {
      backgroundColor: '#052E24',
      borderColor: '#0F6E56',
      textColor: '#D5F0E4',
      accentColor: '#5DCAA5',
    },
  },
  blue: {
    light: {
      backgroundColor: '#E6F1FB',
      borderColor: '#85B7EB',
      textColor: '#0C447C',
      accentColor: '#185FA5',
    },
    dark: {
      backgroundColor: '#08203B',
      borderColor: '#378ADD',
      textColor: '#B5D4FA',
      accentColor: '#B5D4F4',
    },
  },
  purple: {
    light: {
      backgroundColor: '#EEEDFE',
      borderColor: '#CEC8F6',
      textColor: '#3C3489',
      accentColor: '#534AB7',
    },
    dark: {
      backgroundColor: '#14123E',
      borderColor: '#534AB7',
      textColor: '#CEC8F6',
      accentColor: '#AFA9EC',
    },
  },
};

const claimStatusFamilyMap: Record<string, StatusChipFamilyKey> = {
  [CLAIM_STATUS.PAID]: 'green',
  [CLAIM_STATUS.WRITTEN_OFF]: 'gray',
  [CLAIM_STATUS.PARTIALLY_PAID]: 'amber',
  [CLAIM_STATUS.SUBMITTED]: 'blue',
  [CLAIM_STATUS.IN_PROGRESS]: 'teal',
  [CLAIM_STATUS.CANCELLED]: 'gray',
  // [CLAIM_TARIFF_STATUS.PAID]: 'green', // Same key as CLAIM_STATUS.PAID, so using green for that, no duplicate key allowed in object
  [CLAIM_TARIFF_STATUS.WRITTEN_OFF]: 'gray',
  [CLAIM_TARIFF_STATUS.HISTORICAL]: 'gray',
  [CLAIM_TARIFF_STATUS.REJECTED]: 'amber',
  [CLAIM_TARIFF_STATUS.UNMATCHED]: 'amber',
  [CLAIM_TARIFF_STATUS.UNDERPAID]: 'amber',
  [CLAIM_TARIFF_STATUS.OVERPAID]: 'amber',
  [CLAIM_TARIFF_STATUS.PENDING_WRITE_OFF]: 'amber',
  [CLAIM_TARIFF_STATUS.REVERTED]: 'amber',
  [CLAIM_TARIFF_STATUS.UNINSURED]: 'amber',
  [CLAIM_TARIFF_STATUS.INCOMPLETE]: 'amber',
  [CLAIM_TARIFF_STATUS.READY]: 'teal',
  // [CLAIM_TARIFF_STATUS.SUBMITTED]: 'blue', // Same key as CLAIM_STATUS.SUBMITTED, so using blue for that, no duplicate key allowed in object
  [CLAIM_TARIFF_STATUS.PENDING]: 'purple',
  [CLAIM_TARIFF_STATUS.QUERIED]: 'purple',
  [CLAIM_TARIFF_STATUS.PAYOR_INVOICED]: 'purple',
  Draft: 'teal',
  'With biller': 'blue',
  'With public payor': 'purple',
  'With patient': 'purple',
  'With 3rd party': 'purple',
  'Written off': 'gray',
  Unknown: 'amber',
};

const submissionStatusFamilyMap: Record<string, StatusChipFamilyKey> = {
  [SubmissionStatus.cancelled]: 'gray',
  [SubmissionStatus.error]: 'amber',
  [SubmissionStatus.rejected]: 'amber',
  [SubmissionStatus.submitted]: 'blue',
  [SubmissionStatus.pending]: 'purple',
  [SubmissionStatus.received]: 'green',
  [SubmissionStatus.created]: 'teal',
  [SubmissionStatus.generating]: 'gray',
  [SubmissionStatus.reverted]: 'amber',
};

const getChipMode = (colorScheme: string): keyof StatusChipFamily =>
  colorScheme === 'dark' ? 'dark' : 'light';

export const getStatusChipPalette = (
  family: StatusChipFamilyKey,
  colorScheme: string
): StatusChipTone => STATUS_CHIP_PALETTE[family][getChipMode(colorScheme)];

export const getStatusChipFamily = (status: string): StatusChipFamilyKey =>
  claimStatusFamilyMap[status] ?? 'gray';

export const getSubmissionStatusChipFamily = (
  status: string
): StatusChipFamilyKey => submissionStatusFamilyMap[status] ?? 'gray';

export const getStatusChipPresentation = (
  status: string,
  colorScheme: string
): StatusChipTone =>
  getStatusChipPalette(getStatusChipFamily(status), colorScheme);

export const getSubmissionStatusChipPresentation = (
  status: string,
  colorScheme: string
): StatusChipTone =>
  getStatusChipPalette(getSubmissionStatusChipFamily(status), colorScheme);

export const getStatusBadgeColor = (
  status: string,
  colorScheme: string
): string => getStatusChipPresentation(status, colorScheme).textColor;

export const getSubmissionStatusColor = (
  status: string,
  colorScheme: string
): string => getSubmissionStatusChipPresentation(status, colorScheme).textColor;

export const getRegionBadgeColor = (region: string): string => {
  switch (region) {
    case 'Winnipeg':
      return 'blue.7';
    case 'Prairie Mountain':
      return 'green.7';
    case 'Southern':
      return 'lime.7';
    case 'Northern':
      return 'pink';
    case 'Interlake':
      return 'orange.7';
    default:
      return 'blue';
  }
};

export const getEOBActionTypeColor = (actionType: string): string => {
  switch (actionType) {
    case 'R':
      return 'red';
    case 'I':
      return 'green';
    case 'Q':
      return 'violet';
    case 'R/Q':
      return 'orange';
    default:
      return 'gray';
  }
};

export const getGroupTariffColor = (group: string): string => {
  switch (group) {
    case 'Base':
      return 'cyan.6';
    case 'Technical':
      return 'gray';
    case 'Modifier':
      return 'violet.7';
    default:
      return 'blue';
  }
};

export const getFeeBadgeColor = (feesEnabled: boolean): string => {
  switch (feesEnabled) {
    case true:
      return 'green';
    case false:
      return 'red';
  }
};

export const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const getLinearGradient = (
  deg: number,
  color1: string,
  color2: string,
  color3?: string,
  color4?: string,
  color5?: string
) => {
  return `linear-gradient(${deg}deg, ${color1}, ${color2}, ${color3}, ${color4}, ${color5})`;
};

export const getColorForSite = (index: number): string => {
  const colours = [
    'cyan.5',
    'green.5',
    'violet.5',
    'orange.5',
    'pink.5',
    'teal.3',
    'red.5',
    'dark-blue.5',
  ];
  return colours[index % colours.length];
};
